import React, { createRef } from 'react';
import { Vector3, Euler, Quaternion, Matrix4, InstancedMesh } from 'three';
import CornerDirt from './CornerDirt';
import StraightDirt from './StraightDirt';

export type Props = {
  name: string;
  position: Vector3 | [number, number, number];
  width: number;
  height: number;
  modelGeometry: any;
  modelMaterial: any;
  modelScale: number;
  modelYPosition: number;
  modelXRotation: number;
  modelIsDoubled: boolean;
  displayPopover: boolean;
  models: {
    middleDirt: {
      nodes: any;
      materials: any;
    },
    straightDirt: {
      nodes: any;
      materials: any;
    },
    straightVertDirt: {
      nodes: any;
      materials: any;
    },
    cornerDirt: {
      nodes: any;
      materials: any;
    },
  }
}

type State = {
  dummy: number;
};


export default class Field extends React.Component<Props, State> {
  static defaultProps = {
    name: 'Field',
    position: [0, 0, 0],
    width: 10,
    height: 10,
    displayPopover: false,
    modelScale: 1,
    modelIsDoubled: false,
    modelYPosition: 0,
    modelXRotation: 0,
  };

  static CABBAGE_DISTANCE: number = 2;
  static DIRT_CORNER_TILE_SIZE: number = 13;
  static DIRT_STRAIGHT_TILE_SIZE: number = 10;

  state = {
    dummy: 0,
  };

  modelRef = createRef<InstancedMesh>();
  modelRef2 = createRef<InstancedMesh>();
  middleDirtRef = createRef<InstancedMesh>();
  straightDirtHorizontalTopRef = createRef<InstancedMesh>();
  straightDirtHorizontalTopRef2 = createRef<InstancedMesh>();
  straightDirtHorizontalBottomRef = createRef<InstancedMesh>();
  straightDirtHorizontalBottomRef2 = createRef<InstancedMesh>();

  shouldComponentUpdate(nextProps: Readonly<Props>, nextState: Readonly<State>) {
    if (this.state.dummy !== nextState.dummy) {
      return true;
    }

    return false;
  }

  renderCabbages() {
    
    const matrix = new Matrix4();
    const position = new Vector3();
    const rotation = new Euler(0, 0, 0, 'YXZ');
    const quaternion = new Quaternion();
    const scale = new Vector3(this.props.modelScale, this.props.modelScale, this.props.modelScale);

    for (let iWidth=0; iWidth < this.props.width; iWidth++) {
      for (let iHeight=0; iHeight < this.props.height; iHeight++) {
        position.x = Field.CABBAGE_DISTANCE * iWidth;
				position.y = this.props.modelYPosition;
        position.z = Field.CABBAGE_DISTANCE * iHeight;
        
        rotation.y = Math.random() * 2 * Math.PI;
        rotation.x = this.props.modelXRotation;
        quaternion.setFromEuler(rotation);

        matrix.compose(position, quaternion, scale);

        
        if (this.modelRef && this.modelRef.current) {
          const indexModel =  (iHeight * this.props.width) + iWidth;
          this.modelRef!.current!.setMatrixAt(indexModel, matrix);
        }

        if (this.props.modelIsDoubled && this.modelRef2 && this.modelRef2.current) {
          // add small X offset
          position.x = (Field.CABBAGE_DISTANCE * iWidth) + 0.7;
          // rotate double
          rotation.y = Math.random() * 2 * Math.PI;
          quaternion.setFromEuler(rotation)

          matrix.compose(position, quaternion, scale);

          const indexModel =  (iHeight * this.props.width) + iWidth;
          this.modelRef2!.current!.setMatrixAt(indexModel, matrix);
        }
      }
    }

    if (this.modelRef.current) {
      this.modelRef.current.instanceMatrix.needsUpdate = true
    }
    else {
      setTimeout(() => {
        this.setState({
          dummy: this.state.dummy + 1,
        });
      }, 100);
    }

    return (
      <>
        <instancedMesh castShadow name="Vegetables" ref={this.modelRef} args={[this.props.modelGeometry, this.props.modelMaterial, this.props.width * this.props.height]}>
        </instancedMesh>
        {this.props.modelIsDoubled && (
          <instancedMesh castShadow name="Vegetables 2" ref={this.modelRef2} args={[this.props.modelGeometry, this.props.modelMaterial, this.props.width * this.props.height]}>
          </instancedMesh>
        )}
      </>
    )
  }

  renderHorizontalStraightDirt() {
    if (this.props.width <= 9) return;

    const totalHorizontalTiles = Math.ceil((this.props.width - 9) / 5);

    const matrix = new Matrix4();
    const matrix2 = new Matrix4();
    const position = new Vector3();
    const position2 = new Vector3();
    const rotation = new Euler(0, Math.PI, 0, 'YXZ');
    const rotation2 = new Euler(0, 0, 0, 'YXZ');
    const quaternion = new Quaternion();
    const quaternion2 = new Quaternion();
    const scale = new Vector3(1, 1, 1);
    
    for (let i=0; i < totalHorizontalTiles; i++) {
      const left = Field.DIRT_CORNER_TILE_SIZE + (Field.DIRT_STRAIGHT_TILE_SIZE * i);

      // Set first matrix
      position.x = left;
      position.y = 0.03;
      position.z = 3;
      quaternion.setFromEuler(rotation);

      matrix.compose(position, quaternion, scale);
      const indexModel =  i;
      if (this.straightDirtHorizontalTopRef && this.straightDirtHorizontalTopRef.current) {
        this.straightDirtHorizontalTopRef!.current!.setMatrixAt(indexModel, matrix);
      }
      if (this.straightDirtHorizontalTopRef2 && this.straightDirtHorizontalTopRef2.current) {
        this.straightDirtHorizontalTopRef2!.current!.setMatrixAt(indexModel, matrix);
      }
      
      // Set 2nd matrix
      position2.x = left;
      position2.y = 0.04;
      position2.z = this.props.height * Field.CABBAGE_DISTANCE - 5;

      quaternion2.setFromEuler(rotation2);

      matrix2.compose(position2, quaternion2, scale);
      if (this.straightDirtHorizontalBottomRef && this.straightDirtHorizontalBottomRef.current) {
        this.straightDirtHorizontalBottomRef!.current!.setMatrixAt(indexModel, matrix2);
      }
      if (this.straightDirtHorizontalBottomRef2 && this.straightDirtHorizontalBottomRef2.current) {
        this.straightDirtHorizontalBottomRef2!.current!.setMatrixAt(indexModel, matrix2);
      }
    }

    if (this.straightDirtHorizontalTopRef.current && this.straightDirtHorizontalTopRef2.current) {
      this.straightDirtHorizontalTopRef.current.instanceMatrix.needsUpdate = true
      this.straightDirtHorizontalTopRef2.current.instanceMatrix.needsUpdate = true
    }

    if (this.straightDirtHorizontalBottomRef.current && this.straightDirtHorizontalBottomRef2.current) {
      this.straightDirtHorizontalBottomRef.current.instanceMatrix.needsUpdate = true
      this.straightDirtHorizontalBottomRef2.current.instanceMatrix.needsUpdate = true
    }

    else {
      setTimeout(() => {
        this.setState({
          dummy: this.state.dummy + 1,
        });
      }, 100);
    }

      return(
        <>
          <instancedMesh
            receiveShadow
            name="Straight Dirt Horizontal Top 1"
            ref={this.straightDirtHorizontalTopRef}
            args={[this.props.models.straightDirt.nodes.Straight_Sand.geometry, this.props.models.straightDirt.materials.Sand_Shader, totalHorizontalTiles]}
          >
          </instancedMesh>
          <instancedMesh
            receiveShadow
            name="Straight Dirt Horizontal Top 2"
            ref={this.straightDirtHorizontalTopRef2}
            args={[this.props.models.straightDirt.nodes.Straight_Wooden_Plank.geometry, this.props.models.straightDirt.materials.Wood_Shader, totalHorizontalTiles]}
          ></instancedMesh>

          <instancedMesh
            receiveShadow
            name="Straight Dirt Horizontal Bottom 1"
            ref={this.straightDirtHorizontalBottomRef}
            args={[this.props.models.straightDirt.nodes.Straight_Sand.geometry, this.props.models.straightDirt.materials.Sand_Shader, totalHorizontalTiles]}
          >
          </instancedMesh>
          <instancedMesh
            receiveShadow
            name="Straight Dirt Horizontal Bottom 2"
            ref={this.straightDirtHorizontalBottomRef2}
            args={[this.props.models.straightDirt.nodes.Straight_Wooden_Plank.geometry, this.props.models.straightDirt.materials.Wood_Shader, totalHorizontalTiles]}
          ></instancedMesh>
        </>
      );
  }

  renderVerticalStraightDirt() {
    if (this.props.height <= 9) return;

    const totalVerticalTiles = Math.ceil((this.props.height - 9) / 5);
    const content = [];

    for (let i=0; i < totalVerticalTiles; i++) {
      
      const top = Field.DIRT_CORNER_TILE_SIZE + (Field.DIRT_STRAIGHT_TILE_SIZE * i);
      content.push(
        <StraightDirt
          key={'straight-left-' + i}
          rotation={[0, -Math.PI/2, 0]}
          position={[3, 0.05, top]}
          model= {{
            nodes: this.props.models.straightVertDirt.nodes,
            materials: this.props.models.straightVertDirt.materials,
          }}
        />,
        <StraightDirt
          key={'straight-right-' + i}
          rotation={[0, Math.PI/2, 0]}
          position={[this.props.width * Field.CABBAGE_DISTANCE - 5, 0.06, top]}
          model= {{
            nodes: this.props.models.straightVertDirt.nodes,
            materials: this.props.models.straightVertDirt.materials,
          }}
        />
      )
    }

    return content;
  }

  renderMiddleDirt() {
    if (this.props.height <= 9 || this.props.width <= 9) return;

    const totalHorizontalTiles = Math.ceil((this.props.width - 9) / 5);
    const totalVerticalTiles = Math.ceil((this.props.height - 9) / 5);

    const matrix = new Matrix4();
    const position = new Vector3();
    const rotation = new Euler(0, Math.PI, 0, 'YXZ');
    const quaternion = new Quaternion();
    const scale = new Vector3(1, 1, 1);

    for (let iLeft=0; iLeft < totalHorizontalTiles; iLeft++) {
      const left = Field.DIRT_CORNER_TILE_SIZE + (Field.DIRT_STRAIGHT_TILE_SIZE * iLeft);

      for (let iTop=0; iTop < totalVerticalTiles; iTop++) {
        const top = Field.DIRT_CORNER_TILE_SIZE + (Field.DIRT_STRAIGHT_TILE_SIZE * iTop);

        position.x = left;
        position.y = 0.07;
        position.z = top;
        quaternion.setFromEuler(rotation);

        matrix.compose(position, quaternion, scale);
        const indexModel =  (iTop * totalHorizontalTiles) + iLeft;
        if (this.middleDirtRef && this.middleDirtRef.current) {
          this.middleDirtRef!.current!.setMatrixAt(indexModel, matrix);
        }
      }
    }

    if (this.middleDirtRef.current) {
      this.middleDirtRef.current.instanceMatrix.needsUpdate = true
    }

    else {
      setTimeout(() => {
        this.setState({
          dummy: this.state.dummy + 1,
        });
      }, 100);
    }

    return(
      <instancedMesh
        receiveShadow
        name="Middle Dirt"
        ref={this.middleDirtRef}
        args={[
          this.props.models.middleDirt.nodes.Middle_Sand.geometry,
          this.props.models.middleDirt.materials.Sand_Shader,
          totalHorizontalTiles * totalVerticalTiles,
        ]}
      >
      </instancedMesh>
    );
  }

  render() {
    return (
      <group name={`Field ${this.props.name}`} position={this.props.position}>
        {this.renderCabbages()}
        <CornerDirt
          receiveShadow
          rotation={[0, Math.PI, 0]}
          position={[3, 0.01, 3 ]}
          model= {{
            nodes: this.props.models.cornerDirt.nodes,
            materials: this.props.models.cornerDirt.materials,
          }}
        />
        <CornerDirt
          receiveShadow
          position={[this.props.width * Field.CABBAGE_DISTANCE - 5, 0.02, this.props.height * Field.CABBAGE_DISTANCE - 5]}
          model= {{
            nodes: this.props.models.cornerDirt.nodes,
            materials: this.props.models.cornerDirt.materials,
          }}
        />
        <CornerDirt
          rotation={[0, Math.PI/2, 0]}
          position={[this.props.width * Field.CABBAGE_DISTANCE - 5, 0.0, 3]}
          model= {{
            nodes: this.props.models.cornerDirt.nodes,
            materials: this.props.models.cornerDirt.materials,
          }}
        />
        <CornerDirt
          rotation={[0, -Math.PI/2, 0]}
          position={[3, 0, this.props.height * Field.CABBAGE_DISTANCE - 5]}
          model= {{
            nodes: this.props.models.cornerDirt.nodes,
            materials: this.props.models.cornerDirt.materials,
          }}
        />
        {this.renderHorizontalStraightDirt()}
        {this.renderVerticalStraightDirt()}
        {this.renderMiddleDirt()}

        {/* {this.props.displayPopover && (
          <HTML scaleFactor={50}>
            <div className="content">
              {this.props.name}<br />
            </div>
          </HTML>
        )} */}
      </group>
    );
  }
}